import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroBankAccount from "@/assets/hero/hero-business-bank-account.jpg.asset.json";

const HERO = {
  src: heroBankAccount.url,
  alt: "A banker helping a real estate investor open a business bank account at a bright branch office desk, with her LLC formation document folder, laptop, and a rental property key on the table",
};


export const Route = createFileRoute("/business-setup/business-bank-account")({
  head: () =>
    buildHead({
      title: "Business Bank Account for Real Estate Investors | Simply Approved",
      description:
        "Open a business bank account in your LLC's name for reserves, rent deposits, and closing funds on business-purpose investment property loans.",
      url: "/business-setup/business-bank-account",
      type: "article",
      keywords:
        "business bank account for real estate investors, LLC bank account for rental property, business banking for DSCR loan, entity bank account for reserves, rent deposit account LLC, business checking for landlords, bank account for investment property closing, business bank account requirements LLC, separate business and personal finances rental, business banking for foreign national investors, reserve verification underwriting",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "Business bank account", url: "/business-setup/business-bank-account" },
      ],
      service: { name: "Business Banking Setup for Investors" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: BankPage,
});

function BankPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="Open a business bank account for your entity"
        subtitle="Reserves, earnest money, closing funds, and rent deposits should move through an account in the entity's name. Underwriting reviews those statements."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"a business bank account"}
        intro={"Business-purpose loans close in the name of your entity, so the money behind the deal should sit in the entity's name too. A dedicated business account keeps reserves, rent, and closing funds clean and easy to document \u2014 commingling with a personal account is the most common reason files get delayed."}
        reasons={[
          { title: "Reserves are verified from entity statements", body: "Underwriting counts liquid reserves. Statements in the LLC's name are accepted at face value; personal accounts often trigger extra letters of explanation and sourcing requests." },
          { title: "Earnest money and closing funds must match title", body: "Wire instructions, title, and the loan are all in the entity's name. Sending funds from a personal account can hold up escrow at the table." },
          { title: "Rent deposits build a clean income record", body: "Rents flowing into one business account create the deposit history that supports refinances, portfolio loans, and cash-out files later." },
          { title: "It protects the separation you formed the LLC for", body: "Mixing personal and business money undercuts the liability separation you paid to create. Your attorney and CPA will both tell you the same thing." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="Have these ready" title="What banks usually ask for" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "Filed Articles of Organization for the LLC",
            "EIN confirmation letter",
            "Operating Agreement",
            "Government-issued photo ID for each beneficial owner",
            "Business address and, for some banks, a registered agent address",
            "Initial deposit — amounts vary by institution",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Form the LLC and get your EIN confirmation letter from the IRS.",
            "Open the business checking account online with your formation documents and photo ID.",
            "Move reserves and earnest money in, then run every rent payment and property expense through it.",
          ]}
        />
        <PartnerCTA partner={partnerBySlug("lili")!} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/business-setup/business-loans" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">Build business credit</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
